/**
 * Mounts the aiWARE APP_BAR widget (the Redact header bar) and the React pieces that hang
 * off its icons: the notification panel, the download tray and its progress toasts, and a
 * tooltip per widget-owned icon.
 *
 * Rendered above the routed subtree by common-app/pages/RootRouter, for routes whose
 * routes.ts entry sets `showsAppBar` — once per RUN of such routes, not strictly once per
 * session: navigating among them preserves the instance, while entering a route without
 * the flag (the editors) unmounts it and returning remounts it. It is deliberately NOT
 * rendered by the individual pages any more (VE-26729): the bar is identical on every
 * page, but living inside the routed subtree meant each navigation unmounted it, called
 * the SDK's
 * `unmountWidget`, and mounted a fresh widget a tick later — a visible flash of the header
 * on a switch that changes nothing in it. It also took the download tray and the progress
 * toasts down with it mid-download; they now outlive the page that started the batch, the
 * way the batch itself already did.
 *
 * None of what it renders is laid out where it is mounted: the notification panel is a
 * portalled Dialog, the tray is `position: fixed`, ProgressToast renders nothing at all
 * (it drives react-toastify), and each HeaderIconTooltip anchors a popper at a node the
 * widget injected. The bar itself is imperative SDK + DOM work. That is why hoisting this
 * out of the pages is layout-neutral — its position in the tree never mattered.
 *
 * Because the widget config is now read once per run of `showsAppBar` routes rather than
 * once per page, values the mount depends on are read through `latestRef` instead of the
 * first render's closure; see the comment there. The download icon needs no equivalent:
 * its markup is injected hidden and its state is written to the DOM by the effects below,
 * whenever it changes.
 *
 * The REDACT title/logo navigates home through `onClickLogo`, NOT the SDK's default. Given
 * neither `onClickLogo` nor `disableGoHome`, the widget's own handler is
 * `window.location.href = '/'` — a full browser reload, the last one left in the header
 * after VE-26729 converted the side menu to SPA navigation. In folder view the handler
 * also resets the column tree to root when Home is already the current route, because the
 * route dispatch alone is a no-op there; see the comment on it.
 */
import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  actionInitAiware,
  actionShowOrHideNotificationList,
} from '@common-modules/notification/actions';
import NotificationsIcon from '@resources/images/notifications.svg';
import CustomNotification from '../CustomNotification';
import DownloadTray from '../DownloadTray';
import DownloadProgressToast from '../DownloadTray/ProgressToast';
import {
  DOWNLOAD_ACTION_ID,
  buildDownloadIconMarkup,
  setDownloadIconState,
  watchDownloadIcon,
} from './downloadTrayIcon';
import { useStyles } from './notificationBtnStyles';
import { useTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material';
import theme from '@redact/materialUITheme';
import cx from 'classnames';
import { selectSDOs } from '@common-modules/notification';
import { selectIsAppBarMenuOpen } from '@common/state/modules/app';
import { actionOpenOrHideAppBarMenu } from '@common/state/modules/app/actions';
import {
  ROUTE_HOME,
  ROUTE_REDACT_CASEDASHBOARD,
  selectRouteType,
} from '@common-modules/routing';
import {
  TOGGLE_DOWNLOAD_TRAY,
  selectHasDownloadSession,
  selectIsAnyDownloadActive,
} from '@redact-modules/downloads';
import { selectEnableFolderView } from '@common/user-onboarding';
import { actionClearAllSelectedFolders } from '@redact-modules/casemedia/actions';
import { isEmpty } from 'lodash';
import { I18nTranslate } from '@i18n';
import { FALLBACK_VERITONE_APP_ID } from '@helpers/constants';
import HeaderIconTooltip from './HeaderIconTooltip';

/** Mount point for the notifications tooltip, which the widget also owns. */
const NOTIFICATION_ACTION_ID = 'notificationAction';

// Routes that put a heading in the bar's `page-title` slot; every other route leaves it
// empty. Keyed by route type rather than passed in as a prop because the bar outlives the
// page that used to pass it.
const ROUTE_TITLE_MESSAGES: Record<
  string,
  { id: string; defaultMessage: string }
> = {
  [ROUTE_REDACT_CASEDASHBOARD.type]: {
    id: 'caseDashboard',
    defaultMessage: 'Case Dashboard',
  },
};

// The title slot is inside the SDK-owned widget, so it is written by hand rather than
// rendered. No-ops until the widget has mounted; the mount applies the title itself.
const applyPageTitle = (title: string) => {
  const pageTitleElement = document.getElementById('page-title');
  if (!pageTitleElement) {
    return;
  }

  const pageTitle = document.createElement('h2');
  pageTitle.innerText = title;
  pageTitle.style.fontSize = '18px';
  pageTitle.style.fontFamily = 'Dosis';
  pageTitle.style.fontWeight = '600';
  pageTitle.style.textAlign = 'center';
  pageTitle.style.textTransform = 'uppercase';
  pageTitle.style.flex = '1';

  pageTitleElement.firstChild
    ? pageTitleElement.replaceChild(pageTitle, pageTitleElement.firstChild)
    : pageTitleElement.appendChild(pageTitle);
};

const AppBarContent = () => {
  const dispatch = useDispatch();
  const listNotification = useSelector(selectSDOs);
  const classes = useStyles();
  const theme = useTheme();
  const isAppBarMenuOpen = useSelector(selectIsAppBarMenuOpen);
  const routeType = useSelector(selectRouteType);
  const isFolderView = useSelector(selectEnableFolderView);
  const hasDownloadSession = useSelector(selectHasDownloadSession);
  const isDownloadActive = useSelector(selectIsAnyDownloadActive);
  const intl = I18nTranslate.Intl();
  const widgetIdRef = useRef<string>();

  const titleMessage = ROUTE_TITLE_MESSAGES[routeType];
  const title = titleMessage ? intl.formatMessage(titleMessage) : '';

  const notificationCount =
    listNotification?.filter(
      (noti) => !(noti.deleted || noti.status === 'cancelled')
    )?.length || 0;

  const CustomNotificationIcon = `
    <div id="${NOTIFICATION_ACTION_ID}">
      <span class="${classes.notificationBtn}">
        <!-- Decorative: HeaderIconTooltip names the action wrapper above, and a
             translated name beside an untranslated "notification" would have a
             screen reader announce the icon twice, once in the wrong language. -->
        <img id="notificationImage" alt="" src="${NotificationsIcon}" />
        <span id="notificationBadge" class="${cx(classes.notificationBadge, {
          [classes.showBadge]: notificationCount > 0,
        })} ">
          <span id="notificationCount" class="${classes.notificationCount}">
            ${notificationCount}
          </span>
        </span>
      </span>
    <div>
  `;

  // Everything mountWidgetSDK reads, kept current. The mount runs from a timer — up to a
  // tick after the render that scheduled it — and now only once per session, so reading
  // the first render's closure would bake in whatever the store held at app start (an
  // empty notification list, most visibly) with no later remount to correct it.
  const widgetInputs = {
    title,
    notificationCount,
    notificationIcon: CustomNotificationIcon,
    isAppBarMenuOpen,
    routeType,
    isFolderView,
  };
  const latestRef = useRef(widgetInputs);
  latestRef.current = widgetInputs;

  // Wait till the aiware.js has loaded to the window object
  useEffect(() => {
    // Returns true only once the widget is ACTUALLY mounted, so a failed attempt keeps
    // getting retried by the interval below. Reporting success merely because
    // `window.aiware` exists left the app bar permanently missing when a mount attempt
    // failed, since nothing ever tried again.
    const mountWhenSDKReady = () => {
      if (!window.aiware) {
        return false;
      }

      if (!window.isAiwareInitialized) {
        window.isAiwareInitialized = true;
        dispatch(actionInitAiware(window.aiware));
        const apiRoot = window.config?.apiRoot || '';
        const graphQLEndpoint = window.config?.graphQLEndpoint || '';
        const applicationId =
          window.config?.veritoneAppId || FALLBACK_VERITONE_APP_ID;
        const knowledgeBaseUrl =
          'https://support.veritone.com/s/article/000002119';

        window.aiware.init({
          applicationId,
          baseUrl: `${apiRoot}/${graphQLEndpoint}`,
          // Specify both keys until SDK team cleans up the config
          knowledgeBaseUrl,
          knowledgeBaseURL: knowledgeBaseUrl,
          withAuth: true,
          // betaFeatures: true,
        });
      }

      if (isEmpty(widgetIdRef.current)) {
        mountWidgetSDK();
      }

      return !isEmpty(widgetIdRef.current);
    };

    // Always go through the timer — never mount synchronously. The first mount is the one
    // that calls `aiware.init()`, and the app bootstraps the instant `window.aiware`
    // appears, so calling init + mountWidget in the same tick as the first commit gives the
    // SDK no grace period and the bar never appears.
    //
    // The other reason this used to matter — that a page switch unmounted the previous
    // AppBar, and mounting the next one raced the SDK's async teardown — is now rare
    // rather than gone: navigation among `showsAppBar` routes no longer touches this
    // component (VE-26729), but an editor round-trip still unmounts and remounts it, and
    // there the interval's retrying is what recovers a mount that lost that race.
    const intervalId = setInterval(() => {
      if (mountWhenSDKReady()) {
        clearInterval(intervalId);
      }
    }, 250);

    return () => {
      clearInterval(intervalId);
      if (widgetIdRef.current) {
        window.aiware?.unmountWidget(widgetIdRef.current);
        widgetIdRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The bar persists across navigation, so the title has to follow the route rather than
  // arrive with a fresh mount.
  useEffect(() => {
    applyPageTitle(title);
  }, [title]);

  useEffect(() => {
    const notificationNewCount =
      listNotification?.filter(
        (noti) => !(noti.deleted || noti.status === 'cancelled')
      )?.length || 0;
    const notificationBadge = document.getElementById('notificationBadge');
    const notificationCount = document.getElementById('notificationCount');
    if (notificationCount && notificationBadge) {
      if (notificationNewCount > 0) {
        notificationBadge.style.display = 'block';
        notificationCount.innerHTML = notificationNewCount.toString();
      } else {
        notificationBadge.style.display = 'none';
      }
    }
  }, [listNotification]);

  // VE-26426: the app-bar widget is mounted imperatively and does not re-render
  // from React state, so the download icon's visibility and spin are written to
  // the DOM — the same approach the notification badge above takes.
  useEffect(() => {
    setDownloadIconState({
      visible: hasDownloadSession,
      spinning: isDownloadActive,
    });
  }, [hasDownloadSession, isDownloadActive]);

  // ...and all of it re-asserted whenever the widget rebuilds its DOM from the
  // markup it captured at mount, which says "hidden" and would otherwise strand
  // the icon off screen for the rest of a download.
  //
  // Only while a session exists. With none, the markup the widget re-injects is
  // already right — it says hidden, and hidden is what we want — so watching the
  // document through every folder-view and editor re-render buys nothing.
  useEffect(
    () => (hasDownloadSession ? watchDownloadIcon() : undefined),
    [hasDownloadSession]
  );

  const mountWidgetSDK = () => {
    const {
      title: currentTitle,
      notificationCount: currentNotificationCount,
      notificationIcon,
    } = latestRef.current;

    widgetIdRef.current = window.aiware?.mountWidget(
      {
        name: 'APP_BAR',
        elementId: 'redact-app-bar',
        config: {
          title: 'REDACT',
          backgroundColor: theme.palette.primary.main,
          help: true,
          // Unconditional. This is the burger that opens MenuDrawer — the whole side nav
          // (Home, Redaction Codes, Statuses, Profiles, Trash) — so it is not a
          // redaction-codes feature. It used to read the `redactionCodes` flag, which
          // meant turning that flag off removed the ONLY in-app route to Statuses,
          // Profiles and Trash, while leaving the Redaction Codes page, route and menu
          // entry perfectly reachable: exactly inverted. The flag now gates the Redaction
          // Codes entry in MenuDrawer and its route in routes.ts instead.
          leftNav: true,
          zIndex: 1000,
          // Go home as an SPA navigation. Without this the widget falls back to its own
          // `window.location.href = '/'`, which reloads the whole app — the same cost
          // VE-26729 removed from the side menu. Safe to close over `dispatch` (stable);
          // everything else is read through the ref, since this handler outlives the
          // render that created it.
          onClickLogo: () => {
            dispatch(ROUTE_HOME());

            // Already on Home in folder view, the dispatch above changes nothing: the
            // routing saga early-returns on an unchanged type+payload and the page does
            // not remount, so a user several columns deep would click the logo and watch
            // nothing happen. Reset the tree to root instead — that is what "home" means
            // in a hierarchy. It sticks because CaseFolders' last-open restore runs once
            // per mount AND bails when anything is selected, so nothing pulls the chain
            // back. The persisted last-open folder is deliberately left alone: this is a
            // within-session jump, not "forget where I was".
            //
            // Coming from another route, the navigation itself remounts the page and the
            // restore then reopens the remembered chain — continuity is the better
            // behaviour there, so this only fires when there is no navigation to make.
            const { isFolderView: folderView, routeType: currentRouteType } =
              latestRef.current;
            if (folderView && currentRouteType === ROUTE_HOME.type) {
              dispatch(actionClearAllSelectedFolders());
            }
          },
          // This handler lives for the whole session, so it reads the drawer state
          // through the ref rather than closing over the value at mount. No
          // `redactionCodes` guard — see `leftNav` above.
          onClickAppbarMenu: () => {
            dispatch(
              actionOpenOrHideAppBarMenu(!latestRef.current.isAppBarMenuOpen)
            );
          },
          betaFeatures: true,
          notification: false,
          displaySupportChat: true,
          isActive: currentNotificationCount > 0,
          rightActions: [
            {
              label: buildDownloadIconMarkup(classes),
              onClick: function (event: Event) {
                event.preventDefault();
                dispatch(TOGGLE_DOWNLOAD_TRAY());
              },
            },
            {
              label: notificationIcon,
              onClick: function (event: Event) {
                event.preventDefault();
                dispatch(actionShowOrHideNotificationList());
              },
            },
          ],
          logo: true,
          elevation: 0,
          searchBarMountId: 'page-title',
        },
      },
      () => {
        window.aiware?.on('openSupport', async () => {
          try {
            const { initChatWithSupport, chatWithSupport } = await import(
              // eslint-disable-next-line import/no-unresolved
              /* webpackIgnore: true */ 'https://get.aiware.com/veritone-support/latest/2/index.js'
            );
            await initChatWithSupport();
            // TODO: Unclear whether this is supposed to be async or not
            // eslint-disable-next-line @typescript-eslint/await-thenable
            await chatWithSupport();
          } catch (error) {
            console.error('Failed to load chat support:', error);
          }
        });
      }
    );
    const menuIconElement = document.querySelector<HTMLElement>(
      '[class*="iconButton"]'
    );
    if (menuIconElement) {
      menuIconElement.style.padding = '0px';
    }

    const appIconElements =
      document.querySelectorAll<HTMLElement>('[class*="barIcon"]');
    if (appIconElements) {
      appIconElements.forEach((element) => {
        element.style.padding = '4px';
      });
    }

    applyPageTitle(currentTitle);
  };

  return (
    <>
      <CustomNotification />
      <DownloadTray />
      <DownloadProgressToast />
      <HeaderIconTooltip
        nodeId={DOWNLOAD_ACTION_ID}
        title={intl.formatMessage({
          id: 'downloadTrayTitle',
          defaultMessage: 'Downloads',
        })}
      />
      <HeaderIconTooltip
        nodeId={NOTIFICATION_ACTION_ID}
        title={intl.formatMessage({
          id: 'notificationsTooltip',
          defaultMessage: 'Notifications',
        })}
      />
    </>
  );
};

const AppBar = () => (
  <ThemeProvider theme={theme}>
    <AppBarContent />
  </ThemeProvider>
);

export default AppBar;
